import { forwardRef } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Custom styles for SoftInput
import SoftInputRoot from "components/SoftInput/SoftInputRoot";
import SoftInputWithIconRoot from "components/SoftInput/SoftInputWithIconRoot";
import SoftInputIconBoxRoot from "components/SoftInput/SoftInputIconBoxRoot";
import SoftInputIconRoot from "components/SoftInput/SoftInputIconRoot";
import SoftInputWithGroupRoot from "components/SoftInput/SoftInputWithGroupRoot";
import SoftInputGroupBoxRoot from "components/SoftInput/SoftInputGroupBoxRoot";
import SoftInputGroupRoot from "components/SoftInput/SoftInputGroupRoot";

// Soft UI Dashboard React contexts
import { useSoftUIController } from "context";

const SoftInput = forwardRef(({ size, icon, error, success, disabled, group, ...rest }, ref) => {
  let template;
  const [controller] = useSoftUIController();
  const { direction } = controller;
  const iconDirection = icon.direction;
  const groupDirection = group.direction;

  if (group.component && group.direction === "left") {
    template = (
      <SoftInputWithGroupRoot ref={ref} ownerState={{ error, success, disabled }}>
        <SoftInputGroupBoxRoot ownerState={{ size }}>
          <SoftInputGroupRoot fontSize="small" ownerState={{ size }}>
            {group.component}
          </SoftInputGroupRoot>
        </SoftInputGroupBoxRoot>
        <SoftInputRoot
          {...rest}
          ownerState={{ size, error, success, groupDirection, direction, disabled }}
        />
      </SoftInputWithGroupRoot>
    );
  } else if (group.component && group.direction === "right") {
    template = (
      <SoftInputWithGroupRoot ref={ref} ownerState={{ error, success, disabled }}>
        <SoftInputRoot
          {...rest}
          ownerState={{ size, error, success, groupDirection, direction, disabled }}
        />
        <SoftInputGroupBoxRoot ownerState={{ size }}>
          <SoftInputGroupRoot fontSize="small" ownerState={{ size }}>
            {group.component}
          </SoftInputGroupRoot>
        </SoftInputGroupBoxRoot>
      </SoftInputWithGroupRoot>
    );
  } else if (icon.component && icon.direction === "left") {
    template = (
      <SoftInputWithIconRoot ref={ref} ownerState={{ error, success, disabled }}>
        <SoftInputIconBoxRoot ownerState={{ size }}>
          <SoftInputIconRoot fontSize="small" ownerState={{ size }}>
            {icon.component}
          </SoftInputIconRoot>
        </SoftInputIconBoxRoot>
        <SoftInputRoot
          {...rest}
          ownerState={{ size, error, success, iconDirection, direction, disabled }}
        />
      </SoftInputWithIconRoot>
    );
  } else if (icon.component && icon.direction === "right") {
    template = (
      <SoftInputWithIconRoot ref={ref} ownerState={{ error, success, disabled }}>
        <SoftInputRoot
          {...rest}
          ownerState={{ size, error, success, iconDirection, direction, disabled }}
        />
        <SoftInputIconBoxRoot ownerState={{ size }}>
          <SoftInputIconRoot fontSize="small" ownerState={{ size }}>
            {icon.component}
          </SoftInputIconRoot>
        </SoftInputIconBoxRoot>
      </SoftInputWithIconRoot>
    );
  } else {
    template = (
      <SoftInputRoot {...rest} ref={ref} ownerState={{ size, error, success, disabled }} />
    );
  }

  return template;
});

// Setting default values for the props of SoftInput
SoftInput.defaultProps = {
  size: "medium",
  icon: {
    component: false,
    direction: "none",
  },
  error: false,
  success: false,
  disabled: false,
  group: {
    component: false,
    direction: "none",
  },
};

// Typechecking props for the SoftInput
SoftInput.propTypes = {
  size: PropTypes.oneOf(["small", "medium", "large"]),
  icon: PropTypes.shape({
    component: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
    direction: PropTypes.oneOf(["none", "left", "right"]),
  }),
  error: PropTypes.bool,
  success: PropTypes.bool,
  disabled: PropTypes.bool,
  group: PropTypes.shape({
    component: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
    direction: PropTypes.oneOf(["none", "left", "right"]),
  }),
};

export default SoftInput;
